/**
 * Display formatting. Money arrives as integer minor units (paise) and dates as business-local
 * `YYYY-MM-DD`; nothing here does date arithmetic or rounds money (CLAUDE.md rules 1 and 3).
 */

const fractionDigits = (locale: string, currency: string) =>
  new Intl.NumberFormat(locale, { style: "currency", currency }).resolvedOptions().maximumFractionDigits ?? 2;

/** `formatMoney(354000, "INR", "en-IN")` → `₹3,540`. Shows the minor part only when it is not zero. */
export function formatMoney(minor: number, currency: string, locale: string): string {
  if (!Number.isSafeInteger(minor)) throw new RangeError("formatMoney needs an integer amount of minor units");
  const digits = fractionDigits(locale, currency);
  const unit = 10 ** digits;
  const sign = minor < 0 ? -1 : 1;
  const abs = Math.abs(minor);
  const major = Math.trunc(abs / unit);
  const rest = abs % unit;
  const fmt = new Intl.NumberFormat(locale, {
    style: "currency",
    currency,
    minimumFractionDigits: rest === 0 ? 0 : digits,
    maximumFractionDigits: rest === 0 ? 0 : digits,
  });
  // Whole major units are exact in a double; the minor part is added as an exact decimal string.
  if (rest === 0) return fmt.format(sign * major);
  return fmt.format(sign * Number(`${major}.${String(rest).padStart(digits, "0")}`));
}

/** Compact thousands for chart axes: 12000 minor-units-of-1000 style labels are built by the caller. */
export function formatCount(n: number, locale: string, cap = 99): string {
  return n > cap ? `${cap}+` : new Intl.NumberFormat(locale).format(n);
}

const DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

export type DateStyle = "short" | "weekday" | "long" | "full";

/** Formats a business-local `YYYY-MM-DD`. The date is pinned to UTC only so no zone can shift it. */
export function formatDate(ymd: string, locale: string, style: DateStyle = "weekday"): string {
  const m = DATE_RE.exec(ymd);
  if (!m) throw new RangeError(`formatDate needs YYYY-MM-DD, got ${ymd}`);
  const at = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3])));
  const opts: Record<DateStyle, Intl.DateTimeFormatOptions> = {
    short: { day: "numeric", month: "short" },
    weekday: { weekday: "short", day: "numeric", month: "short" },
    long: { weekday: "long", day: "numeric", month: "long" },
    full: { weekday: "long", day: "numeric", month: "long", year: "numeric" },
  };
  const parts = new Intl.DateTimeFormat(locale, { ...opts[style], timeZone: "UTC" }).formatToParts(at);
  // "Mon 2 Nov", never "Mon, 2 Nov": join the parts the way the reference writes them.
  const get = (t: string) => parts.find((p) => p.type === t)?.value;
  return [get("weekday"), get("day"), get("month"), get("year")].filter(Boolean).join(" ");
}

/** An instant shown on the wall clock of `timeZone`, 24-hour, as `09:10`. */
export function formatTime(epochMs: number, timeZone: string, locale = "en-GB"): string {
  return new Intl.DateTimeFormat(locale, { hour: "2-digit", minute: "2-digit", hourCycle: "h23", timeZone }).format(epochMs);
}

/** `HH:MM` or `HH:MM:SS` stays as it is, trimmed to `HH:MM`. */
export function formatClock(hhmm: string): string {
  return /^\d{2}:\d{2}/.test(hhmm) ? hhmm.slice(0, 5) : hhmm;
}

/** Chart axis labels: `₹5K`. Display only, so the divide by the currency's minor unit is fine here. */
export function formatMoneyCompact(minor: number, currency: string, locale: string): string {
  if (!Number.isSafeInteger(minor)) throw new RangeError("formatMoneyCompact needs an integer amount of minor units");
  const unit = 10 ** fractionDigits(locale, currency);
  return new Intl.NumberFormat(locale, { style: "currency", currency, notation: "compact", maximumFractionDigits: 1, minimumFractionDigits: 0 }).format(minor / unit);
}
